import axios from 'axios';
import { log } from 'console';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { createContext, ReactNode, useEffect, useState } from 'react';

const apiBaseUrl = window.location.origin; // process.env.REACT_APP_API_BASE_URL;
const uiBaseUrl = window.location.origin; // 'http://localhost:3000';

const config = {
    authority: `${apiBaseUrl}`, // URL base del servidor de identidad (issuer)
    client_id: 'migglo-ui', // Identificador del cliente configurado en el servidor
    redirect_uri: `${uiBaseUrl}/oidc-callback`, // URL a la que se redirigirá después del login
    response_type: 'code', // Usamos el flujo de código de autorización (PKCE se maneja internamente)
    scope: 'openid', // Ámbitos solicitados (en tu openid-configuration se soporta "openid")
    post_logout_redirect_uri: `${uiBaseUrl}`, // URL a la que se redirige después del logout
    metadata: { // Forzamos endpoints que ya conocemos
        authorization_endpoint: `${apiBaseUrl}/oauth2/authorize`,
        token_endpoint: `${apiBaseUrl}/oauth2/token`,
        userinfo_endpoint: `${apiBaseUrl}/userinfo`,
        jwks_uri: `${apiBaseUrl}/oauth2/jwks`,
        end_session_endpoint: `${apiBaseUrl}/connect/logout` // XXX cfillol: Error sid inválido al hacer logout en el primer navegador si se abre sesion del mismo usuario en 2 navegadores
    },
    userStore: new WebStorageStateStore({ store: window.sessionStorage }), // Si deseas forzar el uso de los endpoints que ya conoces, puedes especificarlos

    // Activamos la renovación silenciosa del token
    automaticSilentRenew: true, 
    silent_redirect_uri: `${uiBaseUrl}/silent-renew.html`
};

export type UserWithRoles = User & { roles: string[] };

function getUserWithRoles(user: User): UserWithRoles {
    const userWithRoles = user as UserWithRoles;
    userWithRoles.roles = parseJwt(user.access_token)['roles'];
    return userWithRoles;
}

function parseJwt(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

const ROLE_BY_NAME = {
    'ADMIN': 0,
    'TECHNICIAN': 1,
    'OWNER': 2,
    'DELEGATE': 3,
    'EMPLOYEE': 4
}

export enum Role {
    ADMIN = 0,
    TECHNICIAN = 1,
    OWNER = 2,
    DELEGATE = 3,
    EMPLOYEE = 4
}

// TODO: Implement is role in domain. For now, we only check the global roles
function is(user: UserWithRoles | null, role: Role, domainId?: string): boolean {
    if (!user) return false;
    if (!user.roles) return false;
    if (role < 0) return false;
    for (const r of user.roles) {
        if (role >= ROLE_BY_NAME[r as keyof typeof ROLE_BY_NAME]) {
            return true;
        }
    }
    return false;
}


////////////////////////////////////////
// Auth context

interface AuthContextProps {
    user: UserWithRoles | null;
    accessToken: string;
    is: (role: Role, domainId?: string) => boolean;
    logout: () => void;
}

export const Auth = createContext<AuthContextProps>({
    user: null,
    accessToken: '',
    is: () => false,
    logout: () => { }
} as AuthContextProps);

export const AuthProvider = (props: { children: ReactNode }) => {

    const [user, setUser] = useState<UserWithRoles | null>(null);
    const [accessToken, setAccessToken] = useState<string>('');
    useEffect(initAuth, []); // Execute only once on component mount

    function initAuth() {
        console.log('Init auth');

        function checkUser(user: User | null): boolean {
            if (!user || user.expired) {
                if (!user) console.log('No logged user');
                else console.log('User token expired');
    
                axios.defaults.headers.common['Authorization'] = '';
                setAccessToken('');
                
                // XXX cfillol: Not needed. Full page will be reloaded after login redirect
                //setUser(null);
    
                (window as any).oidc.login()
                return false;
            } else {
                console.log('Setting token: ', user.access_token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${user.access_token}`;
                if (accessToken!=user.access_token) {
                    setAccessToken(user.access_token);
                    setUser(getUserWithRoles(user));
                }
                
                return true;
            }
        }

        axios.interceptors.response.use(response => response, error => {
            if (error.response.status === 401) {
                console.log('Unauthorized');
                checkUser(null);
            }
            return Promise.reject(error);
        });

        const userManager = new UserManager(config);
        userManager.events.addUserLoaded((user) => { 
            checkUser(user);
        });
        userManager.events.addAccessTokenExpired(() => {
            console.warn('Expirated token. Redirecting to login');
            login();
        });

        async function login() {
            console.log('Login');
            userManager.clearStaleState();
            userManager.signinRedirect();
        }
    
        function logout() {
            userManager.signoutRedirect();
        }
    
        // Global object to access the auth methods from js and html
        (window as any).oidc = { logout, userManager, login };

        userManager.stopSilentRenew(); // On init, we do manual renew so we stop the automatic one
        if (window.location.pathname === '/oidc-callback') {
            console.log('Signin callback received');
            userManager.signinRedirectCallback().then((user) => {
                console.log('Success login');
                window.history.replaceState({}, '', '/');
                checkUser(user);
                userManager.startSilentRenew(); // Start the automatic renew after the manual success one
            });
        } else {
            console.log('Not callback');
            userManager.getUser().then(user => {
                if (checkUser(user)) {
                    console.log('User already logged in');
                    userManager.startSilentRenew(); // Start the automatic renew if the user is already logged in
                }
            });
        }
    }

    const value: AuthContextProps = {
        user,
        accessToken,
        is: (role: Role, domainId?: string) => (user && is(user, role, domainId)) || false,
        logout: () => (window as any).oidc.logout()
    };

    return (
        <Auth.Provider value={value}>
            {props.children}
        </Auth.Provider>
    );
};